import type { Middleware } from "@reduxjs/toolkit";
import { configureStore } from "@reduxjs/toolkit";
import {
	save as saveToLocalStorageMiddleware,
	load as loadFromLocalStorageMiddleware,
} from "redux-localstorage-simple";
import { unknownToError } from "@dhau/lang-extras";
import { defaultsDeep, omit } from "lodash-es";
import { captureMessage as sentryCaptureMessage } from "@sentry/react";
import { initialState as navInitialState } from "~/features/nav/store-slice.ts";
import type { StoreServices } from "~/store-config.ts";
import type { AppState } from "./root-reducer.ts";
import rootReducer from "./root-reducer.ts";
import type { PersistCountryService } from "./persist-country.ts";

const errorHandlerMiddleware: Middleware<unknown, AppState> =
	() => (next) => (action: unknown) => {
		if (
			typeof action === "object" &&
			!!action &&
			"type" in action &&
			typeof action.type === "string" &&
			action.type.endsWith("/rejected") &&
			"error" in action
		) {
			const error = unknownToError(action.error);
			/* eslint-disable-next-line no-console */
			console.warn("Unhandled thunk error", action.error);
			sentryCaptureMessage(`${error.name}: ${error.message}`);
		}
		return next(action);
	};

type PreloadedState = Partial<AppState>;

const localStorageNamespace = "brick.me-creator-app";

const localStorageStates = ["nav.hasDismissedMobileWarning"];

// Intentionally inline the middleware builder in configureStore calls to avoid
// cross-version GetDefaultMiddleware type identity issues.

function createStore(services: StoreServices, preloadedState?: PreloadedState) {
	return configureStore({
		reducer: rootReducer,
		middleware: (getDefaultMiddleware) =>
			getDefaultMiddleware({
				thunk: { extraArgument: services },
				immutableCheck: false,
				serializableCheck: false,
			}).prepend(errorHandlerMiddleware),
		preloadedState,
	});
}

function createSaveCountryCodeMiddleware(
	persistCountryService: PersistCountryService,
): Middleware<unknown, AppState> {
	return (store) => (next) => (action) => {
		const returnValue = next(action);
		const state = store.getState();
		if (state.user.selectedCountryCode) {
			persistCountryService.set(state.user.selectedCountryCode);
		}

		return returnValue;
	};
}

function createStoreWithLocalStoragePersistence(
	services: StoreServices,
	preloadedState: PreloadedState,
) {
	const { persistCountryService } = services;
	const fullPreloadedState: PreloadedState = defaultsDeep(
		omit(preloadedState, "nav"),
		loadFromLocalStorageMiddleware({
			namespace: localStorageNamespace,
			states: localStorageStates,
			disableWarnings: true,
		}),
		{
			nav: preloadedState.nav ?? navInitialState,
		},
	);
	return configureStore({
		reducer: rootReducer,
		middleware(getDefaultMiddleware) {
			const base = getDefaultMiddleware({
				thunk: { extraArgument: services },
				immutableCheck: false,
				serializableCheck: false,
			}).prepend(errorHandlerMiddleware);

			return base
				.concat(
					// redux-localstorage-simple's typings are generic over any state; cast to our AppState
					saveToLocalStorageMiddleware({
						namespace: localStorageNamespace,
						states: localStorageStates,
					}) as unknown as Middleware<unknown, AppState>,
				)
				.concat(createSaveCountryCodeMiddleware(persistCountryService));
		},
		preloadedState: fullPreloadedState,
	});
}

export type { PreloadedState };
export { createStore, createStoreWithLocalStoragePersistence };
